/* A1 — Empresas (Painel Administrativo).
 *
 * Ligada ao endpoint real `/api/empresas` (Fase 0): a empresa como entidade
 * (cadastro) juntada ao assistant (configuração) pelo `sender`.
 *
 * REAL e editável: nome, responsável, contato, plano, servidor, criada em,
 *   acesso do cliente (nº de usuários), instrução e modelo do assistente,
 *   chave OpenAI (booleano).
 * FASE 4 (marcado): reatribuir servidor e o multi-número. */

import React, { useEffect, useMemo, useState } from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { Button, Icon, IconButton, Select, Tag } from './ui';

function fmtData(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch {
    return '—';
  }
}

function comStatus(e) {
  const ativa = e.chaves?.openai && e.chaves?.evoapi;
  return {
    ...e,
    status: ativa ? { tone: 'success', rotulo: 'Ativa' } : { tone: 'warning', rotulo: 'Config pendente' },
    criadaEm: fmtData(e.criado_em),
    temChaveOpenai: !!e.chaves?.openai,
  };
}

export default function Empresas() {
  const recurso = useRecurso((sinal) => api.empresas(sinal), []);
  const empresas = useMemo(() => (recurso.dados?.itens || []).map(comStatus), [recurso.dados]);

  const [selecionadaId, definirSelecionadaId] = useState(null);
  const [cadastrando, definirCadastrando] = useState(false);
  const selecionada = empresas.find((e) => e.empresa_id === selecionadaId) || null;

  useEffect(() => {
    if (!selecionadaId && empresas.length) definirSelecionadaId(empresas[0].empresa_id);
  }, [empresas, selecionadaId]);

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flex: '0 0 auto', padding: '22px 24px 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ margin: 0, font: 'var(--type-h1)', letterSpacing: 'var(--ls-tight)' }}>Empresas</h1>
          <p style={{ margin: 0, font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
            Cada empresa-cliente usa exatamente um servidor Evolution e recebe o próprio painel.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button variant="secondary" icon="filter">Filtros</Button>
          <Button variant="primary" icon="plus" onClick={() => definirCadastrando(true)}>Cadastrar empresa</Button>
        </div>
      </div>

      {cadastrando && (
        <CadastrarEmpresa aoFechar={() => definirCadastrando(false)} aoCriada={() => recurso.recarregar()} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.7fr) 400px', gap: 20, flex: 1, minHeight: 0, padding: '0 24px 24px' }}>
        <Tabela
          empresas={empresas}
          carregando={recurso.carregando}
          erro={recurso.erro}
          recarregar={recurso.recarregar}
          selecionadaId={selecionadaId}
          aoSelecionar={definirSelecionadaId}
        />
        {selecionada ? (
          <Detalhe
            key={selecionada.empresa_id}
            empresa={selecionada}
            aoFechar={() => definirSelecionadaId(null)}
            aoSalvar={() => recurso.recarregar()}
          />
        ) : (
          <aside style={cartao}><VazioLateral /></aside>
        )}
      </div>
    </div>
  );
}

const cartao = {
  display: 'flex', flexDirection: 'column', minHeight: 0,
  background: 'var(--surface-card)', border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
};

const th = {
  position: 'sticky', top: 0, zIndex: 1, textAlign: 'left', padding: '12px 20px',
  background: 'var(--surface-card)', font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)',
  color: 'var(--text-muted)', letterSpacing: 'var(--ls-wide)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap',
};

function Tabela({ empresas, carregando, erro, recarregar, selecionadaId, aoSelecionar }) {
  return (
    <section style={cartao}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flex: '0 0 auto', padding: '16px 20px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <h3 style={{ margin: 0, font: 'var(--type-title)', letterSpacing: 'var(--ls-snug)' }}>
          {carregando ? 'Carregando…' : `${empresas.length} ${empresas.length === 1 ? 'empresa cadastrada' : 'empresas cadastradas'}`}
        </h3>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 32, padding: '0 12px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', color: 'var(--text-placeholder)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>
          <Icon name="search" size={14} />
          Buscar empresa
        </span>
      </header>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {erro ? (
          <Mensagem titulo="As empresas não carregaram" texto={erro.mensagem} acao={erro.temporario ? 'Tentar de novo' : null} aoAcao={recarregar} />
        ) : !carregando && empresas.length === 0 ? (
          <Mensagem titulo="Nenhuma empresa cadastrada" texto="Cadastre a primeira empresa para o painel começar a receber conversas." />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>
            <thead>
              <tr>
                <th style={th}>Empresa</th>
                <th style={th}>Plano</th>
                <th style={th}>Servidor</th>
                <th style={{ ...th, textAlign: 'right' }}>Números</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Criada em</th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((e) => {
                const ativa = e.empresa_id === selecionadaId;
                return (
                  <tr key={e.empresa_id} onClick={() => aoSelecionar(e.empresa_id)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)', background: ativa ? 'var(--surface-active)' : 'transparent', boxShadow: ativa ? 'inset 3px 0 0 var(--accent)' : 'none' }}>
                    <td style={{ padding: '13px 20px', color: 'var(--text-primary)', fontWeight: ativa ? 'var(--fw-semibold)' : 'var(--fw-medium)' }}>{e.nome}</td>
                    <td style={{ padding: '13px 20px', color: 'var(--text-secondary)' }}>{e.plano || '—'}</td>
                    <td style={{ padding: '13px 20px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-label)' }}>{e.servidor || '—'}</td>
                    <td className="nb-tab" style={{ padding: '13px 20px', textAlign: 'right', color: 'var(--text-secondary)' }}>{e.numeros}</td>
                    <td style={{ padding: '13px 20px' }}><Tag tone={e.status.tone} dot>{e.status.rotulo}</Tag></td>
                    <td className="nb-tab" style={{ padding: '13px 20px', textAlign: 'right', color: 'var(--text-muted)' }}>{e.criadaEm}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

const h4 = { margin: 0, font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-muted)', letterSpacing: 'var(--ls-caps)', textTransform: 'uppercase' };
const legenda = { font: 'var(--type-caption)', color: 'var(--text-muted)' };
const linhaChave = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 12px', background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' };
const campoStyle = { height: 38, padding: '0 12px', width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' };

function Detalhe({ empresa, aoFechar, aoSalvar }) {
  const [responsavel, setResponsavel] = useState(empresa.responsavel || '');
  const [contato, setContato] = useState(empresa.contato || '');
  const [plano, setPlano] = useState(empresa.plano || '');
  const [cepBase, setCepBase] = useState(empresa.cep_base || '');
  const [raioKm, setRaioKm] = useState(empresa.raio_km == null ? '' : String(empresa.raio_km));
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [erro, setErro] = useState(null);

  // A personalidade (prompt/modelo) é do CLIENTE (aba Agentes). Aqui é só
  // leitura, para o dono dar suporte.
  const instrucoes = empresa.instrucoes || '';
  const modelo = empresa.modelo || '—';

  const raioOriginal = empresa.raio_km == null ? '' : String(empresa.raio_km);
  const mudou = responsavel !== (empresa.responsavel || '') || contato !== (empresa.contato || '') || plano !== (empresa.plano || '')
    || cepBase !== (empresa.cep_base || '') || raioKm !== raioOriginal;

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      await api.salvarEmpresa(empresa.empresa_id, {
        responsavel, contato, plano,
        // Vazio significa "sem base própria" — vira NULL, não string vazia:
        // é NULL que faz o comportamento cair no global/genérico.
        cep_base: cepBase.trim() || null,
        raio_km: raioKm === '' ? null : Number(raioKm),
      });
      setSalvo(true);
      aoSalvar?.();
      setTimeout(() => setSalvo(false), 2200);
    } catch (e) {
      setErro(e.mensagem || 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <aside style={cartao}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flex: '0 0 auto', padding: '18px 20px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <h3 style={{ margin: 0, font: 'var(--type-h3)', letterSpacing: 'var(--ls-snug)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{empresa.nome}</h3>
          <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{empresa.id || `emp_${empresa.empresa_id}`}{empresa.plano ? ` · ${empresa.plano}` : ''}</span>
        </div>
        <IconButton icon="x" label="Fechar" onClick={aoFechar} />
      </header>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 22, padding: '18px 20px 24px' }}>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h4 style={h4}>Dados cadastrais</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(0,1fr)', gap: '10px 12px', alignItems: 'center', fontSize: 'var(--fs-body-sm)' }}>
            <span style={{ color: 'var(--text-muted)' }}>Nome</span><span style={{ color: 'var(--text-primary)' }}>{empresa.nome}</span>
            <span style={{ color: 'var(--text-muted)' }}>Responsável</span><input style={campoStyle} value={responsavel} onChange={(e) => setResponsavel(e.target.value)} placeholder="—" />
            <span style={{ color: 'var(--text-muted)' }}>Contato</span><input style={campoStyle} value={contato} onChange={(e) => setContato(e.target.value)} placeholder="—" />
            <span style={{ color: 'var(--text-muted)' }}>Plano</span><input style={campoStyle} value={plano} onChange={(e) => setPlano(e.target.value)} placeholder="—" />
            <span style={{ color: 'var(--text-muted)' }}>Criada em</span><span style={{ color: 'var(--text-secondary)' }}>{empresa.criadaEm}</span>
          </div>
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h4 style={h4}>Alcance geográfico</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(0,1fr)', gap: '10px 12px', alignItems: 'center', fontSize: 'var(--fs-body-sm)' }}>
            <span style={{ color: 'var(--text-muted)' }}>CEP da sede</span>
            <input style={campoStyle} value={cepBase} onChange={(e) => setCepBase(e.target.value)} placeholder="00000-000" />
            <span style={{ color: 'var(--text-muted)' }}>Raio (km)</span>
            <input style={campoStyle} value={raioKm} onChange={(e) => setRaioKm(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="—" />
          </div>
          <span style={legenda}>Define quando a Sofia pode se apresentar citando a instituição pelo nome. Em branco, ela se apresenta sempre de forma genérica — o lado seguro.</span>
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h4 style={h4}>Servidor Evolution</h4>
          <div style={{ ...linhaChave }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-body-sm)', color: 'var(--text-primary)' }}>
              <Icon name="server" size={14} /> {empresa.servidor || '— sem servidor'}
            </span>
            <Tag tone="neutral">1 empresa : 1 servidor</Tag>
          </div>
          <span style={legenda}>Reatribuir servidor é gerenciado na aba Servidores (Fase 4 reconecta os números).</span>
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h4 style={h4}>Personalidade (só leitura)</h4>
          <textarea rows={6} value={instrucoes} readOnly style={{ width: '100%', padding: 12, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)', color: 'var(--text-secondary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', lineHeight: 'var(--lh-relaxed)', resize: 'vertical', outline: 'none' }} />
          <span style={legenda}>O prompt é gerido pelo cliente (aba Agentes do painel dele). Aqui é só para suporte.</span>
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h4 style={h4}>Modelo e chaves</h4>
          <div style={linhaChave}>
            <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>Modelo de IA</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-label)', color: 'var(--text-primary)' }}>{modelo}</span>
          </div>
          <div style={linhaChave}>
            <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>Chave OpenAI da empresa</span>
            <Tag tone={empresa.temChaveOpenai ? 'success' : 'warning'}>{empresa.temChaveOpenai ? 'Configurada' : 'Não configurada'}</Tag>
          </div>
          <div style={linhaChave}>
            <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>Voz (ElevenLabs)</span>
            <Tag tone="neutral">Herdada da plataforma</Tag>
          </div>
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h4 style={h4}>Acesso do cliente</h4>
          <div style={{ ...linhaChave }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
              <Icon name="user-round" size={14} /> {empresa.usuarios || 0} usuário(s) com acesso
            </span>
            <Tag tone={empresa.usuarios ? 'success' : 'neutral'}>{empresa.usuarios ? 'Ativo' : 'Sem acesso'}</Tag>
          </div>
        </section>
      </div>

      <footer style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto', padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
        <Button variant="primary" onClick={salvar} disabled={salvando || !mudou} style={salvando || !mudou ? { opacity: 0.6, cursor: 'default' } : undefined}>{salvando ? 'Salvando…' : 'Salvar alterações'}</Button>
        <Button variant="ghost" style={{ color: 'var(--danger-text)' }}>Desativar empresa</Button>
        {salvo && <span style={{ marginLeft: 'auto', font: 'var(--type-caption)', color: 'var(--success-text)' }}>Salvo</span>}
        {erro && <span style={{ marginLeft: 'auto', font: 'var(--type-caption)', color: 'var(--danger-text)' }}>{erro}</span>}
      </footer>
    </aside>
  );
}

function Mensagem({ titulo, texto, acao, aoAcao }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, height: '100%', minHeight: 220, padding: 32, textAlign: 'center' }}>
      <span style={{ font: 'var(--type-title)', color: 'var(--text-primary)' }}>{titulo}</span>
      {texto && <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)', maxWidth: 320 }}>{texto}</span>}
      {acao && <span style={{ marginTop: 6 }}><Button variant="secondary" onClick={aoAcao}>{acao}</Button></span>}
    </div>
  );
}

function VazioLateral() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, height: '100%', padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
      <Icon name="building-2" size={22} />
      <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>Selecione uma empresa para ver os detalhes.</span>
    </div>
  );
}

const rot = { font: 'var(--type-label)', color: 'var(--text-secondary)' };
const inp = { height: 40, padding: '0 12px', width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' };

function CampoTexto({ label, valor, aoMudar, placeholder, tipo = 'text' }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={rot}>{label}</span>
      <input type={tipo} style={inp} value={valor} placeholder={placeholder} onChange={(e) => aoMudar(e.target.value)} />
    </label>
  );
}

/* Cadastro de empresa + usuário automático (Fase 1). Ao salvar com e-mail, o
   backend cria empresa + servidor + assistant + usuário e devolve a senha
   temporária uma vez. */
function CadastrarEmpresa({ aoFechar, aoCriada }) {
  const [form, definirForm] = useState({ nome: '', sender: '', assistant_id: '', instancia: '', evoapi_url: '', email: '', responsavel: '', papel: 'gestor' });
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState(null);
  const [pronto, definirPronto] = useState(null);
  const [copiado, definirCopiado] = useState(false);

  const set = (k) => (v) => { definirForm((f) => ({ ...f, [k]: v })); definirErro(null); };
  const valido = form.nome.trim() && form.sender.trim() && form.assistant_id.trim() && form.email.trim() && form.responsavel.trim();

  async function enviar() {
    definirEnviando(true);
    definirErro(null);
    try {
      const r = await api.criarAssistente({
        sender: form.sender.trim(), assistant_id: form.assistant_id.trim(), nome: form.nome.trim(),
        instancia: form.instancia.trim(), evoapi_url: form.evoapi_url.trim(),
        usuario: { email: form.email.trim(), nome: form.responsavel.trim(), papel: form.papel },
      });
      aoCriada?.();
      definirPronto(r.usuario || { email: form.email.trim().toLowerCase(), papel: form.papel, senha_temporaria: null });
    } catch (e) {
      definirErro(e.mensagem || 'Não foi possível cadastrar.');
    } finally {
      definirEnviando(false);
    }
  }

  async function copiar() {
    try { await navigator.clipboard.writeText(pronto.senha_temporaria); definirCopiado(true); setTimeout(() => definirCopiado(false), 1500); } catch { /* sem clipboard */ }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,5,20,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 6 }}>
      <div style={{ ...cartao, width: 540, maxWidth: '100%', maxHeight: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flex: '0 0 auto', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, font: 'var(--type-h3)' }}>{pronto ? 'Empresa cadastrada' : 'Cadastrar empresa'}</h3>
          <IconButton icon="x" label="Fechar" onClick={aoFechar} />
        </header>

        {pronto ? (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--success-text)' }}>
              <Icon name="circle-check" size={20} />
              <span style={{ font: 'var(--type-title)' }}>Acesso do cliente criado</span>
            </div>
            <p style={{ margin: 0, font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
              O painel do cliente já está liberado para <b>{pronto.email}</b> ({pronto.papel}). Envie estes dados — a senha temporária aparece <b>só agora</b>.
            </p>
            {pronto.senha_temporaria && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' }}>
                <span style={{ font: 'var(--type-label)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--ls-caps)' }}>Senha temporária</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body)', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{pronto.senha_temporaria}</code>
                  <Button variant="secondary" icon={copiado ? 'check' : 'key-round'} onClick={copiar} style={{ height: 34, padding: '0 12px' }}>{copiado ? 'Copiado' : 'Copiar'}</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 22, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h4 style={h4}>Dados da empresa</h4>
              <CampoTexto label="Nome da empresa" valor={form.nome} aoMudar={set('nome')} placeholder="Igreja Batista Central" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <CampoTexto label="Identificador (sender)" valor={form.sender} aoMudar={set('sender')} placeholder="5511999998888@s.whatsapp.net" />
                <CampoTexto label="Identificador do assistente" valor={form.assistant_id} aoMudar={set('assistant_id')} placeholder="cfg_igreja" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <CampoTexto label="Instância de WhatsApp" valor={form.instancia} aoMudar={set('instancia')} placeholder="InstanciaIgreja" />
                <CampoTexto label="Servidor Evolution (URL)" valor={form.evoapi_url} aoMudar={set('evoapi_url')} placeholder="https://evo.exemplo.com.br" />
              </div>
            </section>

            <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h4 style={h4}>Acesso do cliente</h4>
              <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', marginTop: -4 }}>Salvar já cria o primeiro usuário e libera o painel do cliente.</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <CampoTexto label="E-mail do responsável" tipo="email" valor={form.email} aoMudar={set('email')} placeholder="pastor@igreja.org" />
                <CampoTexto label="Nome do responsável" valor={form.responsavel} aoMudar={set('responsavel')} placeholder="Pra. Cláudia" />
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={rot}>Papel</span>
                <Select value={form.papel} onChange={(e) => set('papel')(e.target.value)} fullWidth options={[
                  { value: 'gestor', label: 'Administrador da empresa (vê métricas e conversas)' },
                  { value: 'atendente', label: 'Atendente (só conversas)' },
                ]} />
              </label>
            </section>

            {erro && <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--danger-text)' }}>{erro}</span>}
          </div>
        )}

        <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flex: '0 0 auto', padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          {pronto ? (
            <Button variant="primary" onClick={aoFechar}>Concluir</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={aoFechar}>Cancelar</Button>
              <Button variant="primary" onClick={enviar} disabled={!valido || enviando} style={!valido || enviando ? { opacity: 0.6, cursor: 'default' } : undefined}>{enviando ? 'Cadastrando…' : 'Cadastrar empresa'}</Button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
